/**
 * TravelApprovalsPage - Refactored Version
 * Uses shared components and hooks.
 */
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { travelAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { useTable } from '../hooks/useTable';
import { formatDate, formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plane, Train, Car, MapPin, Calendar, IndianRupee, CheckCircle, XCircle, User, Clock, FileText, Loader2, Eye, ChevronRight
} from 'lucide-react';

// Shared components
import { DataTable } from '../components/shared/DataTable';
import { SearchInput } from '../components/shared/SearchInput';
import { PageSkeleton } from '../components/shared/LoadingSkeleton';

// Constants
const TRAVEL_TYPE_CONFIG = {
  local_domestic: { label: 'Local', variant: 'secondary' },
  domestic_outstation: { label: 'Domestic', variant: 'outline' },
  international: { label: 'International', variant: 'default' },
};

const MODE_ICONS = {
  flight: Plane, train: Train, bus: Car, cab: Car, self_drive: Car, other: MapPin,
};

const STATUS_CONFIG = {
  l1_pending: { label: 'L1 Pending', variant: 'secondary' },
  l2_pending: { label: 'L2 Pending', variant: 'outline' },
  l3_pending: { label: 'L3 Pending', variant: 'outline' },
  approved: { label: 'Approved', variant: 'default' },
  rejected: { label: 'Rejected', variant: 'destructive' },
};

export default function TravelApprovalsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [comments, setComments] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Data fetching
  const { data: requests, loading, refresh } = useApi(travelAPI.getPending);

  // Table hook
  const { items: filteredRequests, search, setSearch, sort, setSort } = useTable(requests || [], {
    searchFields: ['employee_name', 'destination', 'purpose'],
    defaultSort: { field: 'created_at', order: 'desc' }
  });

  // Stats
  const stats = useMemo(() => {
    const reqs = requests || [];
    return {
      l1Pending: reqs.filter(r => r.status === 'l1_pending').length,
      l2Pending: reqs.filter(r => r.status === 'l2_pending').length,
      l3Pending: reqs.filter(r => r.status === 'l3_pending').length,
      totalBudget: reqs.reduce((sum, r) => sum + (r.estimated_budget || 0), 0),
    };
  }, [requests]);

  const getApprovalLevel = (request) => {
    if (request.status === 'l1_pending') return 'l1';
    if (request.status === 'l2_pending') return 'l2';
    if (request.status === 'l3_pending') return 'l3';
    return null;
  };

  const openApprovalDialog = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setComments('');
    setDialogOpen(true);
  };

  const handleApproval = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      const level = getApprovalLevel(selectedRequest);
      if (actionType === 'approve') {
        if (level === 'l1') await travelAPI.approveL1(selectedRequest.id, comments);
        else if (level === 'l2') await travelAPI.approveL2(selectedRequest.id, comments);
        else if (level === 'l3') await travelAPI.approveL3(selectedRequest.id, comments);
        toast.success('Travel request approved');
      } else {
        if (level === 'l1') await travelAPI.rejectL1(selectedRequest.id, comments);
        else if (level === 'l2') await travelAPI.rejectL2(selectedRequest.id, comments);
        else if (level === 'l3') await travelAPI.rejectL3(selectedRequest.id, comments);
        toast.success('Travel request rejected');
      }
      setDialogOpen(false);
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${actionType}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Table columns
  const columns = [
    { key: 'employee_name', label: 'Employee', sortable: true, render: (row) => (
      <div className="flex items-center gap-2">
        <User className="w-4 h-4 text-muted-foreground" />
        <span className="font-medium">{row.employee_name || 'Unknown'}</span>
      </div>
    )},
    { key: 'destination', label: 'Destination', render: (row) => (
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-muted-foreground" />
        <span>{row.destination}</span>
      </div>
    )},
    { key: 'travel_type', label: 'Type', render: (row) => {
      const config = TRAVEL_TYPE_CONFIG[row.travel_type] || TRAVEL_TYPE_CONFIG.local_domestic;
      return <Badge variant={config.variant}>{config.label}</Badge>;
    }},
    { key: 'departure_date', label: 'Dates', sortable: true, render: (row) => (
      <div className="flex items-center gap-2 text-sm">
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <span>{formatDate(row.departure_date)} - {formatDate(row.return_date)}</span>
      </div>
    )},
    { key: 'estimated_budget', label: 'Budget', sortable: true, render: (row) => (
      <span className="font-semibold">{formatCurrency(row.estimated_budget)}</span>
    )},
    { key: 'status', label: 'Status', render: (row) => {
      const config = STATUS_CONFIG[row.status] || STATUS_CONFIG.l1_pending;
      return <Badge variant={config.variant}>{config.label}</Badge>;
    }},
    { key: 'actions', label: '', render: (row) => {
      const level = getApprovalLevel(row);
      if (!level) return null;
      return (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); navigate(`/travel/${row.id}`); }}>
            <Eye className="w-4 h-4 mr-1" /> View
          </Button>
          <Button size="sm" variant="outline" className="text-green-600" onClick={(e) => { e.stopPropagation(); openApprovalDialog(row, 'approve'); }}>
            <CheckCircle className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" className="text-red-600" onClick={(e) => { e.stopPropagation(); openApprovalDialog(row, 'reject'); }}>
            <XCircle className="w-4 h-4" />
          </Button>
        </div>
      );
    }}
  ];

  if (loading) return <PageSkeleton />;

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Travel Approvals</h1>
        <p className="text-muted-foreground mt-1">Review and approve travel requests (3-level approval)</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all"
          onClick={() => setSearch('')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">L1 Pending</span>
                </div>
                <p className="text-2xl font-bold">{stats.l1Pending}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all"
          onClick={() => setSearch('')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-muted-foreground text-sm mb-1">L2 Pending</div>
                <p className="text-2xl font-bold">{stats.l2Pending}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all"
          onClick={() => setSearch('')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-muted-foreground text-sm mb-1">L3 Pending</div>
                <p className="text-2xl font-bold">{stats.l3Pending}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-muted-foreground text-sm mb-1">Total Budget</div>
            <p className="text-2xl font-bold">{formatCurrency(stats.totalBudget)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <SearchInput value={search} onChange={setSearch} placeholder="Search by employee, destination..." className="max-w-md" />

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plane className="w-5 h-5" />
            Pending Approvals
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <DataTable
            data={filteredRequests}
            columns={columns}
            sort={sort}
            onSort={setSort}
            emptyMessage="No travel requests pending approval"
            emptyIcon={<CheckCircle className="h-12 w-12 text-green-500" />}
          />
        </CardContent>
      </Card>

      {/* Approval Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? 'Approve' : 'Reject'} Travel Request
            </DialogTitle>
            <DialogDescription>
              {selectedRequest && (
                <>Travel to {selectedRequest.destination} by {selectedRequest.employee_name}</>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg text-sm">
                <div>
                  <p className="text-muted-foreground">Destination</p>
                  <p className="font-medium">{selectedRequest.destination}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Budget</p>
                  <p className="font-medium">{formatCurrency(selectedRequest.estimated_budget)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Dates</p>
                  <p className="font-medium">{formatDate(selectedRequest.departure_date)} - {formatDate(selectedRequest.return_date)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Purpose</p>
                  <p className="font-medium">{selectedRequest.purpose}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{actionType === 'reject' ? 'Reason for Rejection' : 'Comments (Optional)'}</Label>
                <Textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder={actionType === 'reject' ? 'Enter reason...' : 'Any comments...'}
                  required={actionType === 'reject'}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              variant={actionType === 'reject' ? 'destructive' : 'default'}
              onClick={handleApproval}
              disabled={actionLoading || (actionType === 'reject' && !comments.trim())}
            >
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              {actionType === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
